import React, { useState, useContext, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Accordion, Card, Alert, Spinner } from 'react-bootstrap';
import { ThemeContext } from '../context/ThemeContext';
import { useAlert } from '../context/AlertContext';
import clientiService from '../services/clientiService';
import orderService from '../services/orderService';
import configService from '../services/configService';
import EditClienteCredenziali from './EditClienteCredenziali';
import useIsMobile from '../hooks/useIsMobile';

const isOrariobloccoDestZeroish = (v) => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  if (s === '') return true;
  const n = Number(s.replace(',', '.'));
  if (Number.isFinite(n) && n === 0) return true;
  return s === '0' || s === '0,0' || s === '0.0';
};

const normalizeOrarioConfiguratorString = (raw) => {
  if (raw === null || raw === undefined) return '';
  return String(raw).trim().replace(',', '.');
};

const resolveOrariobloccoDestForSave = (destinazione, orarioBloccoGenerale) => {
  const raw = destinazione?.orarioblocco_dest;
  if (!isOrariobloccoDestZeroish(raw)) return normalizeOrarioConfiguratorString(raw);
  const g = normalizeOrarioConfiguratorString(orarioBloccoGenerale);
  return g || '0';
};

const buildDatiDestinazionePayload = (destinazione, orarioBloccoGenerale) => {
  const abilita = String(destinazione.abilita_blocco ?? '0').trim() === '1' ? '1' : '0';
  const orarioblocco_dest =
    abilita === '1'
      ? resolveOrariobloccoDestForSave(destinazione, orarioBloccoGenerale)
      : '0';
  return {
    nomede: destinazione.nomede || '',
    viade: destinazione.viade || '',
    cittade: destinazione.cittade || '',
    provinciade: destinazione.provinciade || '',
    nazionede: destinazione.nazionede || 'IT',
    capde: destinazione.capde || '',
    destlogin: destinazione.destlogin || '',
    destpsw: destinazione.destpsw || '',
    orderactive: destinazione.orderactive || '0',
    lun_act: destinazione.lun_act || '0',
    mar_act: destinazione.mar_act || '0',
    mer_act: destinazione.mer_act || '0',
    gio_act: destinazione.gio_act || '0',
    ven_act: destinazione.ven_act || '0',
    sab_act: destinazione.sab_act || '0',
    dom_act: destinazione.dom_act || '0',
    spec_act: destinazione.spec_act || '0',
    abilita_blocco: abilita,
    orarioblocco_dest: orarioblocco_dest,
  };
};

const isValidEmail = (value) => {
  if (!value) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

/** Stessa email in destlogin su più destinazioni: messaggio per l'utente o null se ok. */
const getDestinazioniDuplicateEmailMessage = (destinazioni) => {
  const list = Array.isArray(destinazioni) ? destinazioni : [];
  const byLower = new Map();
  for (let i = 0; i < list.length; i++) {
    const raw = String(list[i]?.destlogin ?? '').trim();
    if (!raw || !isValidEmail(raw)) continue;
    const k = raw.toLowerCase();
    if (!byLower.has(k)) byLower.set(k, { display: raw, indices: [] });
    byLower.get(k).indices.push(i + 1);
  }
  for (const { display, indices } of byLower.values()) {
    if (indices.length > 1) {
      return `La stessa email non può essere usata su più destinazioni ("${display}" — destinazioni n. ${indices.join(', ')}).`;
    }
  }
  return null;
};

const EditCliente = ({ 
  show, 
  onHide, 
  clienteDettagli, 
  loadingCliente, 
  onClienteUpdate,
  isNewCliente = false
}) => {
  const { darkMode } = useContext(ThemeContext);
  const { showAlertGlobal } = useAlert();
  const [clienteForm, setClienteForm] = useState({});
  const [orarioBloccoGenerale, setOrarioBloccoGenerale] = useState(null);
  const isMobile = useIsMobile();
  const [savingContattoIndex, setSavingContattoIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const handleFormChange = (field, value, arrayField = null, index = null) => {
    setClienteForm(prev => {
      const newForm = { ...prev };
      
      if (arrayField && index !== null) {
        // Gestione array (destinazioni, contatti)
        if (!newForm[arrayField]) {
          newForm[arrayField] = clienteDettagli[arrayField] ? [...clienteDettagli[arrayField]] : [];
        }
        
        if (!newForm[arrayField][index]) {
          newForm[arrayField][index] = { ...clienteDettagli[arrayField][index] };
        }
        
        newForm[arrayField][index][field] = value;
      } else {
        // Gestione campi semplici
        newForm[field] = value;
      }
      
      return newForm;
    });

    setValidationErrors(prev => {
      if (!prev[field] && !(field === 'piva' || field === 'cod_fisc')) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      if (field === 'piva' || field === 'cod_fisc') {
        delete next.piva;
        delete next.cod_fisc;
      }
      return next;
    });
  };

  const handleEmailBlurAutoUsername = () => {
    if (!isNewCliente) return;

    const emailValue = (clienteForm.email || '').trim();
    const usernameValue = (clienteForm.destlogin || '').trim();

    if (isValidEmail(emailValue) && !usernameValue) {
      handleFormChange('destlogin', emailValue);
    }
  };

  // Funzione per aggiungere una nuova destinazione
  const handleAddDestinazione = () => {
    const nuovaDestinazione = {
      nomede: '',
      codicede: '',
      viade: '',
      cittade: '',
      provinciade: '',
      capde: '',
      referente: '',
      isPrimaria: false,
      destlogin: '',  // vuoti per nuova destinazione
      destpsw: '',    // vuoti per nuova destinazione
      isNuovaDestinazione: true,
      orderactive: '0',
      lun_act: '0',
      mar_act: '0',
      mer_act: '0',
      gio_act: '0',
      ven_act: '0',
      sab_act: '0',
      dom_act: '0',
      spec_act: '0',
      abilita_blocco: '0',
      orarioblocco_dest: '',
    };
    
    const currentDestinazioni = clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie || [];
    setClienteForm(prev => ({
      ...prev,
      destinazioni_clie: [...currentDestinazioni, nuovaDestinazione]
    }));
  };

  // Funzione per eliminare una destinazione
  const handleDeleteDestinazione = (index) => {
    if (window.confirm('Sei sicuro di voler eliminare questa destinazione?')) {
      const currentDestinazioni = clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie || [];
      const newDestinazioni = currentDestinazioni.filter((_, i) => i !== index);
      setClienteForm(prev => ({
        ...prev,
        destinazioni_clie: newDestinazioni
      }));
    }
  };

  // Funzione per impostare destinazione primaria
  const handleSetPrimaria = (index) => {
    const currentDestinazioni = clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie || [];
    const newDestinazioni = currentDestinazioni.map((dest, i) => ({
      ...dest,
      isPrimaria: i === index
    }));
    setClienteForm(prev => ({
      ...prev,
      destinazioni_clie: newDestinazioni
    }));
  };

  // Funzione per aggiungere un nuovo contatto
  const handleAddContatto = () => {
    const nuovoContatto = {
      persona: '',
      ruolo: '',
      email: '',
      tel1: '',
      tel2: '',
      cell: '',
      predefinito: "0",
      pec: "0"
    };
    
    const currentContatti = clienteForm.contattiaggiuntivi_clie || clienteDettagli.contattiaggiuntivi_clie || [];
    setClienteForm(prev => ({
      ...prev,
      contattiaggiuntivi_clie: [...currentContatti, nuovoContatto]
    }));
  };

  // Funzione per eliminare un contatto (chiama DELETE se esiste su server, altrimenti solo stato locale)
  const handleDeleteContatto = async (index) => {
    const codiceCliente = clienteDettagli?.c_cliente;
    const contatti = clienteForm.contattiaggiuntivi_clie || clienteDettagli.contattiaggiuntivi_clie || [];
    const contatto = contatti[index];
    if (!contatto) return;

    if (codiceCliente && contatto.id != null && contatto.id !== '') {
      try {
        await clientiService.eliminaContattoAggiuntivo(codiceCliente, contatto.id);
        showAlertGlobal('Contatto eliminato', 'success');
        const updated = await clientiService.getClienti(codiceCliente);
        setClienteForm(prev => ({ ...prev, contattiaggiuntivi_clie: updated?.contattiaggiuntivi_clie ?? [] }));
        if (onClienteUpdate) onClienteUpdate(updated);
      } catch (error) {
        console.error('Errore eliminazione contatto:', error);
        showAlertGlobal(error?.response?.data?.message || error?.message || 'Errore nell\'eliminazione del contatto', 'danger');
      }
    } else {
      const newContatti = contatti.filter((_, i) => i !== index);
      setClienteForm(prev => ({ ...prev, contattiaggiuntivi_clie: newContatti }));
    }
  };

  // Salva un singolo contatto aggiuntivo (inserimento o modifica)
  const handleSalvaContatto = async (index) => {
    if (!clienteDettagli?.c_cliente) return;
    const codiceCliente = clienteDettagli.c_cliente;
    const contatti = clienteForm.contattiaggiuntivi_clie || clienteDettagli.contattiaggiuntivi_clie || [];
    const contatto = contatti[index];
    if (!contatto) return;

    const body = {
      persona: contatto.persona ?? '',
      ruolo: contatto.ruolo ?? '',
      tel1: contatto.tel1 ?? '',
      tel2: contatto.tel2 ?? '',
      cell: contatto.cell ?? '',
      email: contatto.email ?? '',
      predefinito: contatto.predefinito === '1' || contatto.predefinito === 1
    };

    setSavingContattoIndex(index);
    try {
      if (contatto.id != null && contatto.id !== '') {
        await clientiService.modificaContattoAggiuntivo(codiceCliente, contatto.id, body);
        showAlertGlobal('Contatto aggiornato con successo', 'success');
      } else {
        await clientiService.salvaContattoAggiuntivo(codiceCliente, body);
        showAlertGlobal('Contatto aggiunto con successo', 'success');
      }
      const updated = await clientiService.getClienti(codiceCliente);
      if (onClienteUpdate) onClienteUpdate(updated);
    } catch (error) {
      console.error('Errore salvataggio contatto:', error);
      showAlertGlobal(error?.response?.data?.message || error?.message || 'Errore nel salvataggio del contatto', 'danger');
    } finally {
      setSavingContattoIndex(null);
    }
  };

  // Funzione per salvare una singola destinazione
  const handleSalvaDestinazione = async (index) => {
    try {
      // Recupera i dati del cliente e della destinazione
      const codiceCliente = clienteDettagli.c_cliente;
      const destinazioni = clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie || [];
      const destinazione = destinazioni[index];

      // Verifica che il codice destinazione sia presente
      if (!destinazione?.codicede) {
        showAlertGlobal('Codice destinazione mancante! Non è possibile salvare.', 'warning');
        return;
      }

      const dupEmailMsg = getDestinazioniDuplicateEmailMessage(destinazioni);
      if (dupEmailMsg) {
        showAlertGlobal(dupEmailMsg, 'warning');
        return;
      }

      const datiDestinazione = buildDatiDestinazionePayload(destinazione, orarioBloccoGenerale);

      console.log('💾 Salvataggio destinazione:', {
        codiceCliente,
        codiceDestinazione: destinazione.codicede,
        dati: datiDestinazione
      });

      // Chiama il servizio per salvare la destinazione
      await clientiService.salvaDestinazione(codiceCliente, destinazione.codicede, datiDestinazione);

      // Marca la destinazione come non più "nuova" così Username/Password diventano visibili
      const updatedDestinazioni = [...(clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie)];
      updatedDestinazioni[index] = { ...destinazione, isNuovaDestinazione: false };
      setClienteForm(prev => ({ ...prev, destinazioni_clie: updatedDestinazioni }));

      // Mostra feedback di successo
      showAlertGlobal(
        `✅ Destinazione "${destinazione.nomede}" salvata con successo!`, 
        'success'
      );

      if (onClienteUpdate) {
        onClienteUpdate(clienteDettagli);
      }

    } catch (error) {
      console.error('❌ Errore nel salvataggio della destinazione:', error);
      showAlertGlobal(
        `Errore nel salvataggio: ${error.message || 'Errore sconosciuto'}`, 
        'danger'
      );
    }
  };

  // Funzione per salvare le modifiche
  const handleSaveChanges = async () => {
    try {
      const ragioneSociale = (clienteForm.cliente || clienteDettagli?.cliente || '').trim();
      const piva = (clienteForm.piva || clienteDettagli?.piva || '').trim();
      const codiceFiscale = (clienteForm.cod_fisc || clienteDettagli?.cod_fisc || '').trim();
      const email = (clienteForm.email || clienteDettagli?.email || '').trim();
      const newValidationErrors = {};

      if (!ragioneSociale) {
        newValidationErrors.cliente = true;
      }

      if (!piva && !codiceFiscale) {
        newValidationErrors.piva = true;
        newValidationErrors.cod_fisc = true;
      }

      if (!email) {
        newValidationErrors.email = true;
      } else if (!isValidEmail(email)) {
        newValidationErrors.email = true;
      }

      if (isNewCliente) {
        const username = (clienteForm.destlogin || '').trim();
        const password = (clienteForm.destpsw || '').trim();
        if (!username || !password) {
          if (!username) newValidationErrors.destlogin = true;
          if (!password) newValidationErrors.destpsw = true;
        }
      }

      if (Object.keys(newValidationErrors).length > 0) {
        setValidationErrors(newValidationErrors);
        if (newValidationErrors.email && email && !isValidEmail(email)) {
          showAlertGlobal('Email non valida', 'warning');
        } else if (newValidationErrors.cliente) {
          showAlertGlobal('Ragione sociale obbligatoria', 'warning');
        } else if (newValidationErrors.piva || newValidationErrors.cod_fisc) {
          showAlertGlobal('Inserire almeno P.IVA o Codice Fiscale', 'warning');
        } else if (newValidationErrors.destlogin || newValidationErrors.destpsw) {
          showAlertGlobal('Per un nuovo cliente Username e Password sono obbligatori', 'warning');
        }
        return;
      }

      if (!isNewCliente) {
        const destinazioniCheck =
          clienteForm.destinazioni_clie || clienteDettagli?.destinazioni_clie || [];
        const dupEmailMsg = getDestinazioniDuplicateEmailMessage(destinazioniCheck);
        if (dupEmailMsg) {
          showAlertGlobal(dupEmailMsg, 'warning');
          return;
        }
      }

      if (isNewCliente) {
        // Modalità creazione nuovo cliente
        const nuovoClienteData = {
          cliente: clienteForm.cliente || '',
          indirizzo: clienteForm.indirizzo || '',
          citta: clienteForm.citta || '',
          prov: clienteForm.provincia || '',
          cap: clienteForm.cap || '',
          piva: clienteForm.piva || '',
          cod_fisc: clienteForm.cod_fisc || '',
          tel: clienteForm.tel || '',
          categ: clienteForm.categ || '',
          categ2: clienteForm.categ2 || '',
          condi_pag: clienteForm.condi_pag || '',
          email: clienteForm.email || '',
          persona: clienteForm.persona || '',
          cano_sog: Number(clienteForm.cano_sog ?? 1) === 1 ? 1 : 0
        };

        const clienteCreato = await clientiService.createCliente(nuovoClienteData);

        showAlertGlobal('Nuovo cliente creato con successo', 'success');
        
        // Se sono presenti credenziali, salva anche la destinazione predefinita
        if (clienteForm.destlogin || clienteForm.destpsw) {
          try {
            const codiceClienteCreato = clienteCreato.cliente.c_cliente;
            const codiceDestinazione = '0001';
            
            const datiDestinazione = buildDatiDestinazionePayload(
              {
                nomede: clienteForm.cliente || 'Destinazione Principale',
                viade: clienteForm.indirizzo || '',
                cittade: clienteForm.citta || '',
                provinciade: clienteForm.provincia || '',
                nazionede: 'IT',
                capde: clienteForm.cap || '',
                destlogin: clienteForm.destlogin || '',
                destpsw: clienteForm.destpsw || '',
                orderactive: clienteForm.orderactive || '1',
                lun_act: clienteForm.lun_act || '0',
                mar_act: clienteForm.mar_act || '0',
                mer_act: clienteForm.mer_act || '0',
                gio_act: clienteForm.gio_act || '0',
                ven_act: clienteForm.ven_act || '0',
                sab_act: clienteForm.sab_act || '0',
                dom_act: clienteForm.dom_act || '0',
                spec_act: clienteForm.spec_act || '0',
                abilita_blocco: clienteForm.abilita_blocco || '0',
                orarioblocco_dest: clienteForm.orarioblocco_dest,
              },
              orarioBloccoGenerale
            );

            console.log('💾 Salvataggio destinazione per nuovo cliente:', {
              codiceClienteCreato,
              codiceDestinazione,
              dati: datiDestinazione
            });

            // Salva la destinazione con le credenziali
            await clientiService.salvaDestinazione(codiceClienteCreato, codiceDestinazione, datiDestinazione);
            
            // Se username e password sono presenti, invia le credenziali via email
            if (clienteForm.destlogin && clienteForm.destpsw) {
              try {
                const datiCredenziali = {
                  ragionesociale: clienteForm.cliente || 'Cliente',
                  nomedestinazione: clienteForm.cliente || 'Destinazione Principale',
                  username: clienteForm.destlogin,
                  password: clienteForm.destpsw,
                  urlaccesso: window.location.origin
                };

                console.log('📧 Invio automatico credenziali per nuovo cliente:', datiCredenziali);
                
                await orderService.invioCredenzialiCliente(datiCredenziali);
                showAlertGlobal('Cliente, credenziali salvati e credenziali inviate via email con successo!', 'success');
                
              } catch (emailError) {
                console.error('Errore nell\'invio automatico delle credenziali:', emailError);
                showAlertGlobal('Cliente e credenziali salvati con successo! ⚠️ Errore nell\'invio email delle credenziali.', 'warning');
              }
            } else {
              showAlertGlobal('Cliente e credenziali salvati con successo!', 'success');
            }
          } catch (destError) {
            console.error('Errore nel salvataggio delle credenziali:', destError);
            showAlertGlobal(`Cliente creato ma errore nel salvataggio credenziali: ${destError.message}`, 'warning');
          }
        }
        
        if (onClienteUpdate) {
          onClienteUpdate(clienteCreato);
        }
      } else {
        // Modalità modifica cliente esistente
        const codiceCliente = clienteDettagli.c_cliente;
        const clienteAggiornato = {
          cliente: clienteForm.cliente || clienteDettagli.cliente || '',
          indirizzo: clienteForm.indirizzo || clienteDettagli.indirizzo || '',
          citta: clienteForm.citta || clienteDettagli.citta || '',
          prov: clienteForm.provincia || clienteDettagli.provincia || '',
          cap: clienteForm.cap || clienteDettagli.cap || '',
          piva: clienteForm.piva || clienteDettagli.piva || '',
          cod_fisc: clienteForm.cod_fisc || clienteDettagli.cod_fisc || '',
          tel: clienteForm.tel || clienteDettagli.tel || '',
          categ: clienteForm.categ || clienteDettagli.categ || '',
          categ2: clienteForm.categ2 || clienteDettagli.categ2 || '',
          condi_pag: clienteForm.condi_pag || clienteDettagli.condi_pag || '',
          email: clienteForm.email || clienteDettagli.email || '',
          persona: clienteForm.persona || clienteDettagli.persona || '',
          cano_sog: Number(clienteForm.cano_sog ?? clienteDettagli.cano_sog ?? 1) === 1 ? 1 : 0
        };

        const clienteRisposta = await clientiService.updateCliente(codiceCliente, clienteAggiornato);
        
        // Salva automaticamente tutte le destinazioni esistenti
        const destinazioni = clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie || [];
        let destinazioniSalvate = 0;
        let erroriDestinazioni = [];

        if (destinazioni.length > 0) {
          for (let index = 0; index < destinazioni.length; index++) {
            try {
              const destinazione = destinazioni[index];
              
              // Verifica che il codice destinazione sia presente
              if (!destinazione?.codicede) {
                erroriDestinazioni.push(`Destinazione ${index + 1}: Codice destinazione mancante`);
                continue;
              }

              const datiDestinazione = buildDatiDestinazionePayload(destinazione, orarioBloccoGenerale);

              console.log(`💾 Salvataggio automatico destinazione ${index + 1}:`, {
                codiceCliente,
                codiceDestinazione: destinazione.codicede,
                dati: datiDestinazione
              });

              // Salva la destinazione
              await clientiService.salvaDestinazione(codiceCliente, destinazione.codicede, datiDestinazione);
              destinazioniSalvate++;

            } catch (destError) {
              console.error(`Errore nel salvataggio destinazione ${index + 1}:`, destError);
              erroriDestinazioni.push(`Destinazione ${index + 1}: ${destError.message}`);
            }
          }
        }

        // Mostra messaggio di successo appropriato
        if (erroriDestinazioni.length === 0) {
          const messaggioSuccesso = destinazioniSalvate > 0 
            ? `Cliente aggiornato con successo! Salvate anche ${destinazioniSalvate} destinazioni.`
            : 'Cliente aggiornato con successo';
          showAlertGlobal(messaggioSuccesso, 'success');
        } else {
          const messaggioWarning = `Cliente aggiornato. Destinazioni salvate: ${destinazioniSalvate}/${destinazioni.length}. Errori: ${erroriDestinazioni.join(', ')}`;
          showAlertGlobal(messaggioWarning, 'warning');
        }
        
        if (onClienteUpdate) {
          onClienteUpdate(clienteRisposta);
        }
      }
      
      onHide();
    } catch (error) { 
      console.error('Errore durante il salvataggio:', error);
      const apiErrorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        error?.message ||
        (isNewCliente
          ? 'Errore durante la creazione del nuovo cliente'
          : 'Errore durante il salvataggio delle modifiche');
      showAlertGlobal(apiErrorMessage, 'danger');
    }
  };

  // Reset del form quando si apre il modal
  React.useEffect(() => {
    if (show) {
      setValidationErrors({});
      if (isNewCliente) {
        // Inizializza form vuoto per nuovo cliente
        setClienteForm({
          cliente: '',
          indirizzo: '',
          citta: '',
          provincia: '',
          cap: '',
          piva: '',
          cod_fisc: '',
          tel: '',
          email: '',
          categ: '',
          categ2: '',
          condi_pag: '',
          persona: '',
          note: '',
          // Campi per le credenziali
          cano_sog: 1,
          destlogin: '',
          destpsw: '',
          orderactive: '1',
          lun_act: '0',
          mar_act: '0',
          mer_act: '0',
          gio_act: '0',
          ven_act: '0',
          sab_act: '0',
          dom_act: '0',
          spec_act: '0',
          abilita_blocco: '0',
          orarioblocco_dest: '',
        });
      } else if (clienteDettagli) {
        // Inizializza form con dati esistenti per modifica
        setClienteForm({
          ...clienteDettagli,
          destinazioni_clie: clienteDettagli.destinazioni_clie ? [...clienteDettagli.destinazioni_clie] : [],
          contattiaggiuntivi_clie: clienteDettagli.contattiaggiuntivi_clie ? [...clienteDettagli.contattiaggiuntivi_clie] : []
        });
      }
    }
  }, [show, clienteDettagli, isNewCliente]);

  React.useEffect(() => {
    if (!show) return;
    let cancelled = false;
    configService
      .getConfigurations()
      .then((c) => {
        if (!cancelled) setOrarioBloccoGenerale(c?.orarioBlocco ?? null);
      })
      .catch(() => {
        if (!cancelled) setOrarioBloccoGenerale(null);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // CSS per layout mobile
  useEffect(() => {
    if (show && isMobile) {
      const mobileCSS = `
        <style id="editCliente-mobile-styles">
          .modal-fullscreen-mobile {
            width: 100vw !important;
            height: 100vh !important;
            max-width: 100vw !important;
            max-height: 100vh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }
          
          .modal-fullscreen-mobile .modal-content {
            height: 100vh !important;
            border-radius: 0 !important;
            border: none !important;
          }
          
          .modal-fullscreen-mobile .modal-header {
            position: sticky !important;
            top: 0 !important;
            z-index: 1050 !important;
            border-bottom: 1px solid var(--bs-border-color) !important;
          }
          
          .modal-fullscreen-mobile .modal-body {
            flex: 1 !important;
            overflow-y: auto !important;
            padding: 1rem !important;
            -webkit-overflow-scrolling: touch !important;
          }
          
          .modal-fullscreen-mobile .modal-footer {
            position: sticky !important;
            bottom: 0 !important;
            z-index: 1050 !important;
            border-top: 1px solid var(--bs-border-color) !important;
            padding: 0.75rem 1rem !important;
          }
          
          @media (max-width: 991.98px) {
            .form-control, .form-select {
              min-height: 44px !important;
              font-size: 16px !important;
              padding: 0.5rem 0.75rem !important;
            }
            
            .btn {
              min-height: 44px !important;
              padding: 0.75rem 1rem !important;
              font-size: 16px !important;
              border-radius: 0.375rem !important;
            }
            
            .btn-sm {
              min-height: 44px !important;
              padding: 0.5rem 0.75rem !important;
              font-size: 0.875rem !important;
            }
            
            .form-label {
              font-weight: 600 !important;
              margin-bottom: 0.5rem !important;
              font-size: 15px !important;
            }
            
            .card {
              border-radius: 0.5rem !important;
              margin-bottom: 1rem !important;
            }
            
            .accordion-item {
              border-radius: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            
            .accordion-button {
              min-height: 48px !important;
              font-size: 16px !important;
              padding: 0.75rem 1rem !important;
            }
            
            .modal-fullscreen-mobile .modal-footer .btn {
              flex: 1 !important;
              margin: 0 0.25rem !important;
            }
            
            .modal-fullscreen-mobile .modal-footer .btn:first-child {
              margin-left: 0 !important;
            }
            
            .modal-fullscreen-mobile .modal-footer .btn:last-child {
              margin-right: 0 !important;
            }
            
            .row.g-2 > * {
              padding: 0.25rem !important;
            }
            
            .mb-3 {
              margin-bottom: 1rem !important;
            }
            
            .mb-2 {
              margin-bottom: 0.75rem !important;
            }
          }
        </style>
      `;
      
      // Rimuovi CSS precedente se esiste
      const existingStyle = document.getElementById('editCliente-mobile-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      // Aggiungi nuovo CSS
      document.head.insertAdjacentHTML('beforeend', mobileCSS);
    }
    
    return () => {
      // Cleanup CSS quando il componente si smonta o non è più mobile
      const existingStyle = document.getElementById('editCliente-mobile-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [show, isMobile]);

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="lg"
      fullscreen={isMobile}
      className={`${darkMode ? 'modal-dark' : ''} ${isMobile ? 'modal-fullscreen-mobile' : ''}`}
      data-bs-theme={darkMode ? 'dark' : 'light'}
    >
      <Modal.Header closeButton className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <i className="fas fa-user me-2"></i>
          {isNewCliente ? 'Nuovo Cliente' : `Modifica Cliente: ${(clienteForm.cliente || clienteDettagli?.cliente) || ''}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={darkMode ? 'bg-dark text-light' : ''} style={{ 
        maxHeight: isMobile ? 'none' : '70vh', 
        overflowY: isMobile ? 'auto' : 'auto',
        flex: isMobile ? '1' : 'initial'
      }}>
        {loadingCliente ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Caricamento dati cliente...</p>
          </div>
        ) : (isNewCliente || clienteDettagli) ? (
          <div>
            {/* Informazioni principali */}
            <div className="mb-4">
              <h5 className="border-bottom pb-2 d-flex justify-content-between align-items-center">
                <div>
                  <i className="fas fa-info-circle me-2 text-primary"></i>
                  Informazioni Principali
                </div>
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  <strong>Codice Cliente: </strong>
                  {isNewCliente ? 'Generato automaticamente' : (clienteForm.c_cliente || clienteDettagli?.c_cliente || '-')}
                </div>
              </h5>
              <Row>
                {/* Ragione Sociale - estesa su tutta la riga */}
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="w-100 d-flex align-items-center justify-content-between gap-3 mb-2">
                      <strong className="mb-0">Ragione Sociale:</strong>
                      <Form.Check
                        type="switch"
                        id="cliente-attivo-switch"
                        label={Number(clienteForm.cano_sog ?? (isNewCliente ? 1 : clienteDettagli?.cano_sog ?? 0)) === 1 ? 'Attivo' : 'Non Attivo'}
                        checked={Number(clienteForm.cano_sog ?? (isNewCliente ? 1 : clienteDettagli?.cano_sog ?? 0)) === 1}
                        onChange={(e) => handleFormChange('cano_sog', e.target.checked ? 1 : 0)}
                        className={darkMode ? 'text-light mb-0' : 'mb-0'}
                      />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.cliente || ''}
                      onChange={(e) => handleFormChange('cliente', e.target.value)}
                      isInvalid={!!validationErrors.cliente}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                
                {/* Indirizzo - esteso su tutta la riga */}
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Indirizzo:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.indirizzo || ''}
                      onChange={(e) => handleFormChange('indirizzo', e.target.value)}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                
                {/* Città */}
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Città:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.citta || ''}
                      onChange={(e) => handleFormChange('citta', e.target.value)}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                {/* Provincia e CAP - 1/2 e 1/2 */}
                <Col xs={6} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Provincia:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.provincia || ''}
                      onChange={(e) => handleFormChange('provincia', e.target.value)}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>CAP:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.cap || ''}
                      onChange={(e) => handleFormChange('cap', e.target.value)}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                
                {/* P.IVA e Codice Fiscale - 6/12, 6/12 */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>P.IVA:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.piva || ''}
                      onChange={(e) => handleFormChange('piva', e.target.value)}
                      placeholder="Non specificata"
                      isInvalid={!!validationErrors.piva}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Codice Fiscale:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.cod_fisc || ''}
                      onChange={(e) => handleFormChange('cod_fisc', e.target.value)}
                      placeholder="Non specificato"
                      isInvalid={!!validationErrors.cod_fisc}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                
                {/* Telefono ed Email */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Telefono:</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={clienteForm.tel || ''}
                      onChange={(e) => handleFormChange('tel', e.target.value)}
                      placeholder="Non specificato"
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Email:</strong></Form.Label>
                    <Form.Control
                      type="email"
                      value={clienteForm.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      onBlur={handleEmailBlurAutoUsername}
                      placeholder="Non specificata"
                      isInvalid={!!validationErrors.email}
                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                    />
                  </Form.Group>
                </Col>
                
              </Row>
            </div>

            {/* Accordion per dati complessi */}
            <Accordion defaultActiveKey={isNewCliente ? "3" : "0"} data-bs-theme={darkMode ? 'dark' : 'light'}>
              
              {/* Destinazioni */}
              {!isNewCliente && clienteDettagli && clienteDettagli.destinazioni_clie && clienteDettagli.destinazioni_clie.length > 0 && (
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <i className="fas fa-map-marker-alt me-2"></i>
                    Destinazioni ({(clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie).length})
                    <Button 
                      variant="success" 
                      size="sm" 
                      className="ms-auto me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDestinazione();
                      }}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Nuova Destinazione
                    </Button>
                  </Accordion.Header>
                  <Accordion.Body>
                    {(clienteForm.destinazioni_clie || clienteDettagli.destinazioni_clie).map((dest, index) => (
                      <Card key={index} className="mb-1">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Destinazione {index + 1}</strong>
                            {((clienteForm.destinazioni_clie?.[index]?.isPrimaria || dest.isPrimaria)) && (
                              <span className="badge bg-warning text-dark ms-2">Primaria</span>
                            )}
                          </div>
                          <div>
                            <Button 
                              variant="success" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleSalvaDestinazione(index)}
                              title="Salva questa destinazione"
                            >
                              <i className="fas fa-save"></i>
                            </Button>
                            <Button 
                              variant="warning" 
                              size="sm" 
                              className="me-2"
                              onClick={() => handleSetPrimaria(index)}
                              title="Imposta come destinazione primaria"
                            >
                              <i className="fas fa-star"></i>
                            </Button>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDeleteDestinazione(index)}
                              title="Elimina destinazione"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Nome:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={clienteForm.destinazioni_clie?.[index]?.nomede || dest.nomede || ''}
                                  onChange={(e) => handleFormChange('nomede', e.target.value, 'destinazioni_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Codice:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={clienteForm.destinazioni_clie?.[index]?.codicede || dest.codicede || ''}
                                  onChange={(e) => handleFormChange('codicede', e.target.value, 'destinazioni_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Indirizzo:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={clienteForm.destinazioni_clie?.[index]?.viade || dest.viade || ''}
                                  onChange={(e) => handleFormChange('viade', e.target.value, 'destinazioni_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Città:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={clienteForm.destinazioni_clie?.[index]?.cittade || dest.cittade || ''}
                                  onChange={(e) => handleFormChange('cittade', e.target.value, 'destinazioni_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Row>
                                <Col xs={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label><strong>Provincia:</strong></Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={clienteForm.destinazioni_clie?.[index]?.provinciade || dest.provinciade || ''}
                                      onChange={(e) => handleFormChange('provinciade', e.target.value, 'destinazioni_clie', index)}
                                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                    />
                                  </Form.Group>
                                </Col>
                                <Col xs={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label><strong>CAP:</strong></Form.Label>
                                    <Form.Control
                                      type="text"
                                      value={clienteForm.destinazioni_clie?.[index]?.capde || dest.capde || ''}
                                      onChange={(e) => handleFormChange('capde', e.target.value, 'destinazioni_clie', index)}
                                      className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Referente:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={clienteForm.destinazioni_clie?.[index]?.referente || dest.referente || ''}
                                  onChange={(e) => handleFormChange('referente', e.target.value, 'destinazioni_clie', index)}
                                  placeholder="Non specificato"
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          
                          {/* Sezione Credenziali Cliente */}
                          <EditClienteCredenziali 
                            clienteForm={clienteForm}
                            setClienteForm={setClienteForm}
                            darkMode={darkMode}
                            index={index}
                            dest={dest}
                            handleFormChange={handleFormChange}
                            isNewCliente={false}
                            isNuovaDestinazione={dest.isNuovaDestinazione === true}
                            orarioBloccoGenerale={orarioBloccoGenerale}
                          />
                          
                        </Card.Body>
                      </Card>
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* Contatti Aggiuntivi */}
              {!isNewCliente && clienteDettagli && clienteDettagli.contattiaggiuntivi_clie && clienteDettagli.contattiaggiuntivi_clie.length > 0 && (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <i className="fas fa-address-book me-2"></i>
                    Contatti Aggiuntivi ({(clienteForm.contattiaggiuntivi_clie || clienteDettagli.contattiaggiuntivi_clie).length})
                    <Button 
                      variant="success" 
                      size="sm" 
                      className="ms-auto me-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddContatto();
                      }}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Nuovo Contatto
                    </Button>
                  </Accordion.Header>
                  <Accordion.Body>
                    {(clienteForm.contattiaggiuntivi_clie || clienteDettagli.contattiaggiuntivi_clie).map((contatto, index) => (
                      <Card key={index} className="mb-1">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Contatto {index + 1}</strong>
                            {contatto.predefinito === "1" && <span className="badge bg-primary ms-2">Predefinito</span>}
                            {contatto.pec === "1" && <span className="badge bg-info ms-2">PEC</span>}
                          </div>
                          <div className="d-flex gap-1">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSalvaContatto(index)}
                              disabled={savingContattoIndex !== null}
                              title="Salva contatto"
                            >
                              {savingContattoIndex === index ? (
                                <Spinner animation="border" size="sm" className="me-1" />
                              ) : (
                                <i className="fas fa-save me-1"></i>
                              )}
                              Salva
                            </Button>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDeleteContatto(index)}
                              title="Elimina contatto"
                              disabled={savingContattoIndex !== null}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Persona:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.persona || contatto.persona || '')}
                                  onChange={(e) => handleFormChange('persona', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Ruolo:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.ruolo || contatto.ruolo || '')}
                                  onChange={(e) => handleFormChange('ruolo', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Email:</strong></Form.Label>
                                <Form.Control
                                  type="email"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.email || contatto.email || '')}
                                  onChange={(e) => handleFormChange('email', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Row>
                                <Col>
                                  <Form.Check
                                    type="checkbox"
                                    label="Predefinito"
                                    checked={(clienteForm.contattiaggiuntivi_clie?.[index]?.predefinito || contatto.predefinito) === "1"}
                                    onChange={(e) => handleFormChange('predefinito', e.target.checked ? "1" : "0", 'contattiaggiuntivi_clie', index)}
                                    className={darkMode ? 'text-light' : ''}
                                  />
                                </Col>
                                <Col>
                                  <Form.Check
                                    type="checkbox"
                                    label="PEC"
                                    checked={(clienteForm.contattiaggiuntivi_clie?.[index]?.pec || contatto.pec) === "1"}
                                    onChange={(e) => handleFormChange('pec', e.target.checked ? "1" : "0", 'contattiaggiuntivi_clie', index)}
                                    className={darkMode ? 'text-light' : ''}
                                  />
                                </Col>
                              </Row>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Tel1:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.tel1 || contatto.tel1 || '')}
                                  onChange={(e) => handleFormChange('tel1', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Tel2:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.tel2 || contatto.tel2 || '')}
                                  onChange={(e) => handleFormChange('tel2', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                              
                              <Form.Group className="mb-2">
                                <Form.Label><strong>Cellulare:</strong></Form.Label>
                                <Form.Control
                                  type="text"
                                  value={(clienteForm.contattiaggiuntivi_clie?.[index]?.cell || contatto.cell || '')}
                                  onChange={(e) => handleFormChange('cell', e.target.value, 'contattiaggiuntivi_clie', index)}
                                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* Note */}
              {(isNewCliente || (clienteDettagli && clienteDettagli.note)) && (
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <i className="fas fa-sticky-note me-2"></i>
                    Note
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={(clienteForm.note || '')}
                        onChange={(e) => handleFormChange('note', e.target.value)}
                        className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    </Form.Group>
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* Credenziali per nuovo cliente */}
              {isNewCliente && (
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <i className="fas fa-key me-2"></i>
                    Credenziali Cliente
                  </Accordion.Header>
                  <Accordion.Body>
                    {/* Sezione Credenziali Cliente */}
                    <EditClienteCredenziali 
                      clienteForm={clienteForm}
                      setClienteForm={setClienteForm}
                      darkMode={darkMode}
                      index={0}
                      dest={{
                        nomede: clienteForm.cliente || '',
                        codicede: 'DEST01',
                        destlogin: clienteForm.destlogin || '',
                        destpsw: clienteForm.destpsw || '',
                        orderactive: clienteForm.orderactive || '1',
                        lun_act: clienteForm.lun_act || '0',
                        mar_act: clienteForm.mar_act || '0',
                        mer_act: clienteForm.mer_act || '0',
                        gio_act: clienteForm.gio_act || '0',
                        ven_act: clienteForm.ven_act || '0',
                        sab_act: clienteForm.sab_act || '0',
                        dom_act: clienteForm.dom_act || '0',
                        spec_act: clienteForm.spec_act || '0',
                        abilita_blocco: clienteForm.abilita_blocco || '0',
                        orarioblocco_dest: clienteForm.orarioblocco_dest || '',
                      }}
                      handleFormChange={(field, value) => handleFormChange(field, value)}
                      isNewCliente={true}
                      validationErrors={validationErrors}
                      orarioBloccoGenerale={orarioBloccoGenerale}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

            </Accordion>
          </div>
        ) : (
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Nessun dato cliente disponibile
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className={`${darkMode ? 'bg-dark border-secondary' : ''} ${isMobile ? 'd-flex justify-content-between' : ''}`}>
        <Button 
          variant="success" 
          onClick={handleSaveChanges}
          className={isMobile ? 'flex-fill me-2' : ''}
        >
          <i className="fas fa-save me-2"></i>
          {isNewCliente ? 'Crea Cliente' : 'Salva Modifiche'}
        </Button>
        <Button 
          variant="secondary" 
          onClick={onHide}
          className={isMobile ? 'flex-fill ms-2' : ''}
        >
          <i className="fas fa-times me-2"></i>
          Chiudi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditCliente;
